import React from "react";
import { CircleNotch, Ticket, ImageBroken } from "@phosphor-icons/react";

const EVENTVEXA_LOGO = "/eventvexa-logo.png";

export function Logo({ dark = false, className = "", imgClassName = "h-12 w-auto" }) {
  return (
    <div className={`flex items-center ${className}`}>
      <img src={EVENTVEXA_LOGO} alt="EventVexa" className={imgClassName} />
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <CircleNotch className={`animate-spin ${className}`} />;
}

export function PageLoader({ label = "Loading" }) {
  return (
    <div className="w-full grid place-items-center py-24 text-muted-foreground" data-testid="page-loader">
      <div className="flex flex-col items-center gap-3">
        <Spinner className="w-7 h-7 text-primary" />
        <span className="overline">{label}</span>
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action, testid }) {
  return (
    <div className="border border-dashed border-border rounded-lg p-10 text-center" data-testid={testid}>
      <div className="grid place-items-center w-12 h-12 mx-auto rounded-full bg-muted text-muted-foreground mb-4">
        {Icon ? <Icon size={24} /> : null}
      </div>
      <h3 className="font-heading text-lg font-semibold">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

const STATUS_STYLES = {
  approved: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25", active: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25",
  published: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25", verified: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25",
  paid: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25", confirmed: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25",
  pending: "bg-amber-500/15 text-amber-300 border border-amber-500/25", admin_approval_pending: "bg-amber-500/15 text-amber-300 border border-amber-500/25",
  agreement_pending: "bg-amber-500/15 text-amber-300 border border-amber-500/25", verification_pending: "bg-amber-500/15 text-amber-300 border border-amber-500/25",
  pending_verification: "bg-amber-500/15 text-amber-300 border border-amber-500/25", draft: "bg-zinc-500/15 text-zinc-300 border border-zinc-500/25",
  incomplete: "bg-zinc-500/15 text-zinc-300 border border-zinc-500/25", unpublished: "bg-zinc-500/15 text-zinc-300 border border-zinc-500/25",
  rejected: "bg-red-500/15 text-red-300 border border-red-500/25", blocked: "bg-red-500/15 text-red-300 border border-red-500/25", cancelled: "bg-red-500/15 text-red-300 border border-red-500/25",
  refunded: "bg-blue-500/15 text-blue-300 border border-blue-500/25", suspended: "bg-orange-500/15 text-orange-300 border border-orange-500/25",
  completed: "bg-blue-500/15 text-blue-300 border border-blue-500/25",
  correction_required: "bg-orange-500/15 text-orange-300 border border-orange-500/25",
};

export function StatusBadge({ status }) {
  const key = (status || "").toLowerCase();
  const cls = STATUS_STYLES[key] || "bg-muted text-muted-foreground";
  const label = (status || "—").replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${cls}`}>
      {label}
    </span>
  );
}

export function SmartImage({ src, alt, ratio = "video", className = "", rounded = "rounded-lg" }) {
  const [err, setErr] = React.useState(false);
  const ratios = { video: "aspect-video", poster: "aspect-[4/5]", square: "aspect-square", wide: "aspect-[21/9]" };
  return (
    <div className={`relative overflow-hidden bg-muted ${ratios[ratio] || ratios.video} ${rounded} ${className}`}>
      {!err && src ? (
        <img src={src} alt={alt} loading="lazy"
          onError={() => setErr(true)}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-muted-foreground">
          <ImageBroken size={28} />
        </div>
      )}
    </div>
  );
}

export function SectionHeader({ overline, title, action }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        {overline && <div className="overline mb-1">{overline}</div>}
        <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function Stat({ label, value, accent = false }) {
  return (
    <div className={`relative p-5 rounded-lg border ${accent ? "brand-gradient text-white border-transparent shadow-lg shadow-primary/20 shimmer-card" : "bg-card border-border"}`}>
      <div className="relative z-10">
        <div className={`text-xs uppercase tracking-widest ${accent ? "text-white/70" : "text-muted-foreground"}`}>{label}</div>
        <div className="font-heading text-2xl md:text-3xl font-semibold mt-1 tracking-tight">{value}</div>
      </div>
    </div>
  );
}
